/**
 * Bikram Sambat calendar for the browser.
 *
 * The same data table as the database and `Rigo.Calendar.BikramSambat` — literally the same bytes,
 * copied from `packages/bs-calendar/bs-calendar.csv` by the sync script and hash-compared in CI. The
 * legacy system kept three hand-maintained copies of this table which disagreed, so a date
 * converted differently depending on which layer converted it
 * (`design/uploads/05-nepal-compliance.md` §2.2).
 *
 * Conversion here is for **display only**. A posted document's BS date is derived and frozen by the
 * database (ADR-0008); the browser never sends BS components and the server would ignore them.
 */

import { CALENDAR_CSV } from "./calendar-data.ts";

/** BS 2000-01-01 = AD 1943-04-14. Derived six independent ways — see packages/bs-calendar/README.md. */
export const EPOCH_AD = "1943-04-14";

/** Shrawan. The Nepali fiscal year runs Shrawan to Ashad. */
export const FISCAL_YEAR_START_MONTH = 4;

export const MONTH_NAMES = [
  "Baisakh",
  "Jestha",
  "Asar",
  "Shrawan",
  "Bhadra",
  "Asoj",
  "Kartik",
  "Mangsir",
  "Poush",
  "Magh",
  "Falgun",
  "Chaitra",
] as const;

export const MONTH_NAMES_NP = [
  "बैशाख",
  "जेठ",
  "असार",
  "साउन",
  "भदौ",
  "असोज",
  "कार्तिक",
  "मंसिर",
  "पौष",
  "माघ",
  "फाल्गुन",
  "चैत",
] as const;

export interface BsDate {
  readonly year: number;
  readonly month: number;
  readonly day: number;
  /** Whether the Nepal Gazette has settled this month. Projected months can be shown, not posted into. */
  readonly isOfficial: boolean;
}

export class BikramSambatRangeError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "BikramSambatRangeError";
  }
}

const MS_PER_DAY = 86_400_000;
const EPOCH_MS = Date.UTC(1943, 3, 14);

interface Parsed {
  readonly monthLengths: ReadonlyMap<number, readonly number[]>;
  readonly official: ReadonlyMap<number, boolean>;
  readonly byDayNumber: readonly BsDate[];
  readonly firstYear: number;
  readonly lastYear: number;
}

function parse(csv: string): Parsed {
  const monthLengths = new Map<number, readonly number[]>();
  const official = new Map<number, boolean>();

  for (const line of csv.split("\n").slice(1)) {
    if (!line.trim()) continue;
    const cell = line.trim().split(",");
    const year = Number(cell[0]);
    monthLengths.set(
      year,
      Array.from({ length: 12 }, (_, i) => Number(cell[i + 1])),
    );
    official.set(year, cell[13] === "true");
  }

  const years = [...monthLengths.keys()].sort((a, b) => a - b);
  const firstYear = years[0]!;
  const lastYear = years[years.length - 1]!;

  // One entry per day: conversion becomes an array index rather than a loop.
  const byDayNumber: BsDate[] = [];
  for (const year of years) {
    const isOfficial = official.get(year)!;
    const lengths = monthLengths.get(year)!;
    for (let month = 1; month <= 12; month++) {
      for (let day = 1; day <= lengths[month - 1]!; day++) {
        byDayNumber.push({ year, month, day, isOfficial });
      }
    }
  }

  return { monthLengths, official, byDayNumber, firstYear, lastYear };
}

const data = parse(CALENDAR_CSV);

/** The Gregorian range the calendar covers, as ISO dates. */
export const range = {
  firstAd: EPOCH_AD,
  lastAd: isoFromMs(EPOCH_MS + (data.byDayNumber.length - 1) * MS_PER_DAY),
  firstBsYear: data.firstYear,
  lastBsYear: data.lastYear,
} as const;

function isoFromMs(ms: number): string {
  return new Date(ms).toISOString().slice(0, 10);
}

function msFromIso(iso: string): number {
  const [y, m, d] = iso.split("-").map(Number);
  if (y === undefined || m === undefined || d === undefined || Number.isNaN(y * m * d)) {
    throw new BikramSambatRangeError(`'${iso}' is not an ISO date (YYYY-MM-DD).`);
  }
  return Date.UTC(y, m - 1, d);
}

/** Converts an ISO Gregorian date (`YYYY-MM-DD`) to Bikram Sambat. */
export function toBikramSambat(adIso: string): BsDate {
  const index = Math.round((msFromIso(adIso) - EPOCH_MS) / MS_PER_DAY);
  const found = data.byDayNumber[index];
  if (index < 0 || found === undefined) {
    throw new BikramSambatRangeError(
      `No Bikram Sambat date for ${adIso}; the calendar covers ${range.firstAd} to ${range.lastAd}.`,
    );
  }
  return found;
}

/** Converts a Bikram Sambat date to an ISO Gregorian date (`YYYY-MM-DD`). */
export function toGregorian(year: number, month: number, day: number): string {
  if (month < 1 || month > 12) {
    throw new BikramSambatRangeError(
      `Bikram Sambat month ${month} does not exist; months run 1–12.`,
    );
  }
  const lengths = data.monthLengths.get(year);
  if (!lengths) {
    throw new BikramSambatRangeError(
      `The calendar has no data for BS ${year}; it covers BS ${range.firstBsYear} to ${range.lastBsYear}.`,
    );
  }
  if (day < 1 || day > lengths[month - 1]!) {
    throw new BikramSambatRangeError(
      `BS ${year}-${String(month).padStart(2, "0")} has ${lengths[month - 1]} days; ${day} is not one of them.`,
    );
  }

  let days = 0;
  for (let y = range.firstBsYear; y < year; y++) {
    days += data.monthLengths.get(y)!.reduce((a, b) => a + b, 0);
  }
  for (let m = 1; m < month; m++) days += lengths[m - 1]!;
  return isoFromMs(EPOCH_MS + (days + day - 1) * MS_PER_DAY);
}

/** Whether the Nepal Gazette has settled this month. */
export function isOfficial(year: number, month: number): boolean {
  return month >= 1 && month <= 12 && (data.official.get(year) ?? false);
}

export function daysInMonth(year: number, month: number): number {
  if (month < 1 || month > 12) {
    throw new BikramSambatRangeError(
      `Bikram Sambat month ${month} does not exist; months run 1–12.`,
    );
  }
  const lengths = data.monthLengths.get(year);
  if (!lengths) throw new BikramSambatRangeError(`The calendar has no data for BS ${year}.`);
  return lengths[month - 1]!;
}

function fiscalStartYear(bs: BsDate): number {
  return bs.month >= FISCAL_YEAR_START_MONTH ? bs.year : bs.year - 1;
}

/** The fiscal year label, e.g. `2082/83`. */
export function fiscalYearLabel(adIso: string): string {
  const start = fiscalStartYear(toBikramSambat(adIso));
  return `${start}/${String((start + 1) % 100).padStart(2, "0")}`;
}

/** The IRD's `NNNN.NNN` convention — fiscal year 2080/81 is reported as `2080.081`. */
export function irdFiscalYearLabel(adIso: string): string {
  const start = fiscalStartYear(toBikramSambat(adIso));
  return `${start}.${String((start + 1) % 1000).padStart(3, "0")}`;
}

/** Display form, `YYYY-MM-DD`, zero-padded. Matches BsDate.ToString() in the .NET library. */
export function formatBs(bs: BsDate): string {
  return `${String(bs.year).padStart(4, "0")}-${String(bs.month).padStart(2, "0")}-${String(bs.day).padStart(2, "0")}`;
}

/** The raw CSV, exposed so CI can hash it against the other copies. */
export { CALENDAR_CSV };

// Company time: instants in the company's zone and calendar (company-time.ts).
export * from "./company-time.ts";
